import type { Storage } from "../shop/basket.js";

export type ListKind = "wishlist" | "saved";

const KEYS: Record<ListKind, string> = { wishlist: "red.wishlist.v1", saved: "red.saved.v1" };

/**
 * The guest's wish list (heart on priced items) and saved items (any product, including price on request).
 * Product ids only, kept on this device. Unknown ids are ignored when shown.
 */
export class ProductLists {
  private readonly data: Record<ListKind, string[]>;
  private readonly listeners = new Set<() => void>();

  constructor(private readonly storage?: Storage) {
    this.data = { wishlist: this.load("wishlist"), saved: this.load("saved") };
  }

  private load(kind: ListKind): string[] {
    try {
      const raw = JSON.parse(this.storage?.getItem(KEYS[kind]) ?? "[]") as unknown;
      return Array.isArray(raw) ? raw.filter((x): x is string => typeof x === "string").slice(0, 200) : [];
    } catch {
      return [];
    }
  }

  ids(kind: ListKind): readonly string[] {
    return this.data[kind];
  }

  has(kind: ListKind, id: string): boolean {
    return this.data[kind].includes(id);
  }

  /** Adds or removes; returns true when the product is now on the list. */
  toggle(kind: ListKind, id: string): boolean {
    const list = this.data[kind];
    const i = list.indexOf(id);
    if (i >= 0) list.splice(i, 1);
    else list.unshift(id);
    this.storage?.setItem(KEYS[kind], JSON.stringify(list));
    for (const l of this.listeners) l();
    return i < 0;
  }

  onChange(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }
}
